"use client"

import * as React from "react"
import { PhoneInput as BasePhoneInput } from "react-international-phone"
import "react-international-phone/style.css"
import { cn } from "@/lib/utils"

interface PhoneInputProps {
  id?: string
  name?: string
  defaultValue?: string
  placeholder?: string
  disabled?: boolean
  maxLength?: number
  className?: string
  onBlur?: (value: string) => void
  "aria-invalid"?: boolean
  "aria-describedby"?: string
}

/**
 * International phone input with country flag selector.
 * Wraps react-international-phone, styled to match our Input component.
 * Hidden input carries the E.164 value for FormData submission.
 */
function PhoneInput({
  name,
  defaultValue,
  disabled,
  className,
  onBlur,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  ...props
}: PhoneInputProps) {
  const hasInitialValue = !!defaultValue
  const [phone, setPhone] = React.useState(defaultValue ?? "")
  const [interacted, setInteracted] = React.useState(false)
  const [selectedDialCode, setSelectedDialCode] = React.useState("352")
  const showDialCode = hasInitialValue || interacted
  const submittedPhone = React.useMemo(() => {
    const digits = phone.replace(/\D/g, "")
    return digits === selectedDialCode ? "" : phone
  }, [phone, selectedDialCode])

  return (
    <>
      {/* Hidden input carries the full value for FormData */}
      <input type="hidden" name={name} value={submittedPhone} />

      <BasePhoneInput
        defaultCountry="lu"
        preferredCountries={["lu", "be", "de", "fr", "ch"]}
        value={phone}
        onChange={(value, meta) => {
          if (!interacted) setInteracted(true)
          setSelectedDialCode(meta.country.dialCode)
          setPhone(value)
        }}
        disabled={disabled}
        forceDialCode={showDialCode}
        disableFormatting={false}
        inputProps={{
          ...props,
          "aria-invalid": ariaInvalid,
          "aria-describedby": ariaDescribedBy,
          onBlur: onBlur ? () => onBlur(submittedPhone) : undefined,
        }}
        className={cn("phone-input", className)}
        inputClassName="phone-input__input"
        countrySelectorStyleProps={{
          buttonClassName: "phone-input__country-btn",
          dropdownStyleProps: {
            className: "phone-input__dropdown",
            listItemClassName: "phone-input__dropdown-item",
          },
        }}
      />
    </>
  )
}

PhoneInput.displayName = "PhoneInput"

export { PhoneInput }
